import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getHomeViewRole } from '@app/selectors/home';
import { useTour } from '@app/shared/tour';
import type { TourConfig, TourContextValue } from '@app/shared/tour';
import { buildHomeTourSteps } from './homeTourSteps';
import { HOME_TOUR_BUTTONS } from './homeTourContent';
import {
  getHomeTourOverlayColor,
  getHomeTourOverlayOpacity,
  getHomeTourPopoverClass,
} from './homeTourTheme';
import {
  trackHomeTourCompleted,
  trackHomeTourDismissed,
  trackHomeTourPromptShown,
  trackHomeTourSkipped,
  trackHomeTourStarted,
  trackHomeTourStepViewed,
} from './homeTourAnalytics';
import {
  removeHomeSpacesTourProxy,
  resolveHomeSpacesTourElement,
} from './resolveHomeSpacesTourElement';
import { resolveHomeRecentUpdatesTourElement } from './resolveHomeRecentUpdatesTourElement';
import {
  cancelHomeScrollAnim,
  scrollHomeToTop,
  scrollHomeToTourTarget,
} from './homeTourScroll';
import { HOME_TOUR_STEPS } from './homeTourContent';

export const HOME_TOUR_STORAGE_KEY = 'homeTourCompletedByRole';

type UseHomeTourArgs = {
  isReady: boolean;
};

const useHomeTour = ({ isReady }: UseHomeTourArgs): TourContextValue => {
  const viewRole = useSelector(getHomeViewRole);

  const config: TourConfig = useMemo(
    () => ({
      id: 'home',
      storageKey: HOME_TOUR_STORAGE_KEY,
      steps: () => buildHomeTourSteps(viewRole),
      buttons: HOME_TOUR_BUTTONS,
      theme: {
        overlayColor: getHomeTourOverlayColor,
        overlayOpacity: getHomeTourOverlayOpacity,
        popoverClass: getHomeTourPopoverClass,
      },
      analytics: {
        onPromptShown: trackHomeTourPromptShown,
        onStarted: trackHomeTourStarted,
        onStepViewed: trackHomeTourStepViewed,
        onCompleted: trackHomeTourCompleted,
        onSkipped: trackHomeTourSkipped,
        onDismissed: trackHomeTourDismissed,
      },
      scrollStrategy: scrollHomeToTourTarget,
      onHighlighted: ({ stepId, driver: d }) => {
        if (stepId === 'spaces') {
          resolveHomeSpacesTourElement();
          d.refresh();
          return;
        }
        // Re-resolve after feed scroll / Virtuoso remount so the spotlight
        // lands on a CTA still inside the feed clip (not stuck at the top edge).
        if (
          stepId === 'recent-updates-reactions' ||
          stepId === 'recent-updates-comments'
        ) {
          resolveHomeRecentUpdatesTourElement(HOME_TOUR_STEPS[stepId].element);
          d.refresh();
        }
      },
      onAfterDestroy: () => {
        cancelHomeScrollAnim();
        removeHomeSpacesTourProxy();
        scrollHomeToTop();
      },
      animateMs: 700,
      progressText: '{{current}}/{{total}}',
    }),
    [viewRole]
  );

  return useTour(config, viewRole, isReady);
};

export default useHomeTour;
